import PropTypes from 'prop-types';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
// the Universe brand (DESIGN.md §2)
import { UNIVERSE } from '../../../theme/universe/brand';
import { LOW_POWER } from '../../../theme/universe/lowPower';
//
import { SIDEBAR_FONT } from './sidebarTokens';

// ----------------------------------------------------------------------
// The foot of the rail: a star — because the line beside it says you are one
// — in the Universe's own colours, floating on its glow. The planet stays
// where it identifies the portal (the collapsed rail's mark); the star is the
// brand's colour on every portal, by request, so "a star of the Universe"
// reads the same everywhere. It wears the brand gradient — the light blue
// into the navy — since the rail took the brand's colours (07/10/2026).
//
// The line is English on every portal, by request — it is the product's own
// voice, not interface copy.
// ----------------------------------------------------------------------

const HELLO = 'Hello';
const STAR = "You're a star in our Universe";

// The Universe light blue, fixed regardless of the portal's own accent.
const STAR_GLOW = alpha(UNIVERSE.light, 0.5);

function Star() {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden
      sx={{
        position: 'relative',
        width: 44,
        height: 44,
        display: 'block',
        overflow: 'visible',
        filter: `drop-shadow(0 6px 14px ${STAR_GLOW})`
      }}
    >
      <defs>
        {/* The brand gradient, light blue → navy, on every portal. */}
        <linearGradient id="sb-star-universe" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={UNIVERSE.light} />
          <stop offset="1" stopColor={UNIVERSE.navy} />
        </linearGradient>
      </defs>
      <path
        fill="url(#sb-star-universe)"
        d="M12 2.4c.3 0 .6.2.7.5l2.5 5.3 5.8.8c.7.1.9.9.4 1.4l-4.2 4 1 5.8c.1.7-.6 1.2-1.2.9L12 18.3l-5.2 2.8c-.6.3-1.3-.2-1.2-.9l1-5.8-4.2-4c-.5-.5-.2-1.3.4-1.4l5.8-.8 2.6-5.3c.2-.3.5-.5.8-.5Z"
      />
      {/* A small highlight, so it reads as a lit body rather than a flat shape. */}
      <path
        fill="#FFFFFF"
        opacity="0.55"
        d="M9.9 8.7c.9-.4 1.6-1.2 2-2.2.1-.3.5-.3.6 0 .2.6.5 1.1.9 1.5-1.3.3-2.5.5-3.5.7Z"
      />
      {/* Two sparks, the way a star is drawn when it is the point. */}
      <circle cx="19.5" cy="4.5" r="1.1" fill={UNIVERSE.light} opacity="0.9" />
      <circle cx="4.2" cy="19.2" r="0.8" fill={UNIVERSE.light} opacity="0.7" />
    </Box>
  );
}

NavbarPortalCard.propTypes = {
  portal: PropTypes.object.isRequired
};

export default function NavbarPortalCard({ portal }) {
  const { user } = useAuth();

  const firstName = user?.firstName || (user?.name || '').split(' ')[0] || '';

  return (
    <Box sx={{ px: 2, pb: 2.5, pt: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.75}
        sx={{
          p: 1.5,
          pr: 2,
          borderRadius: 14,
          bgcolor: 'var(--sb-card)',
          border: '1px solid var(--sb-glass-border)',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            width: 56,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -4,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${STAR_GLOW} 0%, rgba(0,0,0,0) 68%)`,
              // The gradient is already soft; the blur only rounds it off, and
              // a filter is a buffer. Not on a TV (see `universe/lowPower`),
              // where this rail is open on a wall for days at a time.
              ...(!LOW_POWER && { filter: 'blur(4px)' })
            },
            // A slow breath, not a twinkle: the rail should stay calm. An
            // infinite animation also keeps the compositor awake for as long
            // as the page is open — fine on a desk, not on a board that is
            // never closed, so a television gets the star still.
            ...(!LOW_POWER && {
              '@media (prefers-reduced-motion: no-preference)': {
                '& svg': { animation: 'sbStarBreathe 6s ease-in-out infinite' }
              },
              '@keyframes sbStarBreathe': {
                '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
                '50%': { transform: 'scale(1.06) rotate(-4deg)' }
              }
            })
          }}
        >
          <Star />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: SIDEBAR_FONT,
              fontWeight: 700,
              fontSize: '0.62rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--sb-accent-ink)'
            }}
          >
            {portal.label}
          </Typography>
          <Typography
            noWrap
            sx={{
              mt: 0.25,
              fontFamily: SIDEBAR_FONT,
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'var(--sb-text)'
            }}
          >
            {HELLO}
            {firstName ? `, ${firstName}` : ''}
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontFamily: SIDEBAR_FONT,
              fontSize: '0.72rem',
              lineHeight: 1.4,
              color: 'var(--sb-text-soft)'
            }}
          >
            {STAR}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
